import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useStore, useWorkshop } from "@/state/store";
import { useOffsiteConfig } from "@/engine/app/offsite-context";

/**
 * Timer-Overlay unten rechts (Briefing Prompt-Teil 4.1) — und darüber die
 * Steuerung der Phasen-Uhr für die Moderation als kleine, explizite Knöpfe
 * (Wunsch 15.09. — vorher Klick/Long-Press auf der Pille selbst, das war
 * schlecht zu treffen und nicht selbsterklärend):
 *
 *   ⏸ Stopp   → Pause (Phasenzeile wird gelb, Restzeit friert ein)
 *   ▶ Weiter  → läuft mit der Restzeit weiter
 *   ↺ Neu     → Phase neu starten (frische Soll-Dauer)
 *
 * Die Zeit kommt aus den servergeführten Stempeln; kein eigener Timer-State.
 * In der Arbeitsphase werden Pause/Weiter/Neustart mit dem großen
 * Handy-Timer (timerEndsAt) gekoppelt, damit Leinwand und Handys nie
 * auseinanderlaufen. Farben: grün = läuft, gelb = pausiert, SVO-Blau = unter
 * 60 s Rest. Im Zustand Warten ausgeblendet; Überzug als +mm:ss.
 */

const PHASEN_LABEL: Record<string, string> = {
  einfuehrung: "Einführung",
  thema: "Thema",
  prompten: "Prompten",
  arbeitsphase: "Arbeitsphase",
  generierung: "Generierung",
  vortrag: "Vortrag",
  ergebnisse: "Ergebnisse",
  abschluss: "Abschluss",
};

function fmt(restSek: number): string {
  const ueberzug = restSek < 0;
  const s = Math.abs(Math.trunc(restSek));
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${ueberzug ? "+" : ""}${mm}:${ss}`;
}

export function TimerPille() {
  const { state, dispatch } = useStore();
  const token = state.auth.token;
  const config = useOffsiteConfig();
  const phaseConfig = useWorkshop().phase;
  const [, tick] = useState(0);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  /**
   * Über der Stations-Leiste parken. Deren Höhe ist nicht fix — bei
   * schmaleren Fenstern umbricht sie auf zwei Zeilen (≈130px) und die Pille
   * lag mit festem bottom auf den Zurück/Weiter-Knöpfen (Fund 15.09.).
   */
  const [leisteHoehe, setLeisteHoehe] = useState(64);
  useEffect(() => {
    const leiste = document.querySelector(".stations-leiste");
    if (!leiste) return;
    const ro = new ResizeObserver(() => {
      setLeisteHoehe(leiste.getBoundingClientRect().height);
    });
    ro.observe(leiste);
    return () => ro.disconnect();
  }, []);

  const {
    phase,
    anzeigePhase,
    phaseStartedAt,
    phaseDauerSec,
    gruppeStartedAt,
    gruppeDauerSec,
    phasePausedRestSec,
    timerEndsAt,
    timerPausedRemainingSec,
  } = phaseConfig;

  const anzeige =
    anzeigePhase ??
    (phase === "overnight"
      ? "generierung"
      : phase === "reveal"
        ? "ergebnisse"
        : phase === "working"
          ? "arbeitsphase"
          : phase);

  const pausiert =
    typeof phasePausedRestSec === "number" && phasePausedRestSec > 0;

  /** In der Arbeitsphase hängen die Handys am großen Timer — mitkoppeln. */
  const arbeitsphase = anzeige === "arbeitsphase";
  const grosserTimerLaeuft =
    typeof timerEndsAt === "number" && timerEndsAt > Date.now();
  const grosserTimerPausiert =
    typeof timerPausedRemainingSec === "number" && timerPausedRemainingSec > 0;

  const klick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (pausiert) {
        const r = await api.admin.phasenUhr(token, "weiter");
        dispatch({ type: "phase/set", phase: r.phase });
        if (arbeitsphase && grosserTimerPausiert) {
          const r2 = await api.admin.resumeTimer(token);
          dispatch({ type: "phase/set", phase: r2.phase });
        }
      } else {
        const r = await api.admin.phasenUhr(token, "pause");
        dispatch({ type: "phase/set", phase: r.phase });
        if (arbeitsphase && grosserTimerLaeuft) {
          const r2 = await api.admin.pauseTimer(token);
          dispatch({ type: "phase/set", phase: r2.phase });
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const neustart = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const dauern = config.stationsAblauf?.phaseDauernSec as
        | Record<string, number>
        | undefined;
      const dauer = dauern?.[anzeige] ?? phaseDauerSec ?? 300;
      await api.admin.phasenUhr(token, "neustart", dauer);
      // Zurücksetzen heißt anhalten (Wunsch 15.09.): Die Uhr steht voll auf
      // Soll-Dauer und wartet, bis die Moderation ▶ Start klickt.
      const r = await api.admin.phasenUhr(token, "pause");
      dispatch({ type: "phase/set", phase: r.phase });
      if (arbeitsphase) {
        await api.admin.setTimer(token, dauer, "set");
        const r2 = await api.admin.pauseTimer(token);
        dispatch({ type: "phase/set", phase: r2.phase });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  if (phase === "waiting" || gruppeStartedAt === undefined) return null;

  const now = Date.now();
  const gruppeRest =
    (gruppeDauerSec ?? 25 * 60) - (now - gruppeStartedAt) / 1000;
  const phaseRest = pausiert
    ? phasePausedRestSec!
    : phaseStartedAt !== undefined && phaseDauerSec !== undefined
      ? phaseDauerSec - (now - phaseStartedAt) / 1000
      : null;

  const zustand = pausiert
    ? "is-pausiert"
    : phaseRest !== null && phaseRest > 0 && phaseRest < 60
      ? "is-warnung"
      : "is-laufend";

  return (
    <div
      className="timer-dock"
      style={{ bottom: `calc(${Math.round(leisteHoehe)}px + 2vh)` }}
      data-testid="timer-pille"
    >
      {/* Steuerung über der Pille: klein und explizit, fürs Publikum kaum
       *  sichtbar, für die Moderation eindeutig treffbar. */}
      {phaseRest !== null && (
        <div className="timer-knoepfe">
          <button
            type="button"
            className="timer-knopf"
            data-testid="timer-stopp"
            title={pausiert ? "Phase weiterlaufen lassen" : "Phase anhalten"}
            onClick={() => void klick()}
            disabled={busy}
          >
            {pausiert ? "▶ Start" : "⏸ Stopp"}
          </button>
          <button
            type="button"
            className="timer-knopf"
            data-testid="timer-neustart"
            title="Phase mit voller Soll-Dauer neu starten"
            onClick={() => void neustart()}
            disabled={busy}
          >
            ↺ Zurücksetzen
          </button>
        </div>
      )}
      <div className="timer-pille">
        <div className="timer-pille-zeile">
          <span>Gruppe</span>
          <span className="timer-pille-zeit">{fmt(gruppeRest)}</span>
        </div>
        {phaseRest !== null && (
          <div className={`timer-pille-zeile ${zustand}`}>
            <span>
              {PHASEN_LABEL[anzeige] ?? anzeige}
              {pausiert ? " · Pause" : ""}
            </span>
            <span className="timer-pille-zeit">{fmt(phaseRest)}</span>
          </div>
        )}
      </div>
    </div>
  );
}
